// Prebid.js bid adapter for the mpwa ad platform.
//
// Drop this file into a Prebid.js custom build, or load it as an external
// adapter via `pbjs.registerBidAdapter()` from a page script. It speaks the
// same Prebid.js BidAdapter contract as the built-in adapters — `isBidRequestValid`,
// `buildRequests`, `interpretResponse`, `getUserSyncs` — and translates
// to/from our `/v1/openrtb/auction` OpenRTB 2.6 endpoint.
//
// Usage in a page:
//
//   pbjs.que.push(function () {
//     pbjs.addAdUnits([{
//       code: 'banner-1',
//       mediaTypes: { banner: { sizes: [[300, 250]] } },
//       bids: [{
//         bidder: 'mpwa',
//         params: { adUnitId: 1, endpoint: 'https://ads.mpwa.to/v1/openrtb/auction' },
//       }],
//     }]);
//     pbjs.requestBids({ timeout: 300, bidsBackHandler: ... });
//   });

export const BIDDER_CODE = 'mpwa';
export const DEFAULT_ENDPOINT = 'https://ads.mpwa.to/v1/openrtb/auction';
export const DEFAULT_CURRENCY = 'USD';

/**
 * Prebid.js BidRequest (the wrapper hands one of these to the adapter per
 * `bid` entry in an AdUnit). Only the fields we read are typed here.
 */
export interface PrebidBidRequest {
  bidder: string;
  bidId: string;
  adUnitCode: string;
  auctionId: string;
  params: {
    adUnitId: number | string;
    endpoint?: string;
    bcat?: string[];
    badv?: string[];
    test?: 0 | 1;
  };
  mediaTypes?: {
    banner?: { sizes?: Array<[number, number]>; pos?: number };
    video?: { playerSize?: Array<[number, number]>; mimes?: string[]; maxduration?: number };
  };
  sizes?: Array<[number, number]>;
  ortb2?: Record<string, unknown>;
  ortb2Imp?: Record<string, unknown>;
  userIdAsEids?: Array<{ source: string; uids: Array<{ id: string }> }>;
}

/** Prebid.js wraps the BidRequest array in a "BidderRequest" with auction-level info. */
export interface PrebidBidderRequest {
  bidderCode: string;
  auctionId: string;
  bidderRequestId: string;
  timeout: number;
  refererInfo?: { page?: string; topmostLocation?: string; domain?: string };
  gdprConsent?: { gdprApplies: boolean; consentString: string };
  uspConsent?: string;
}

/**
 * What we hand back to Prebid.js after building the server request. Prebid
 * dispatches the call itself; we just describe what to send.
 */
export interface PrebidServerRequest {
  method: 'POST';
  url: string;
  data: unknown;
  options: { contentType: 'application/json'; withCredentials: boolean };
  /** Carried back into interpretResponse for impid->bidId mapping. */
  bids: PrebidBidRequest[];
}

/** Subset of what we return to Prebid.js after a winning bid is interpreted. */
export interface PrebidInterpretedBid {
  requestId: string;
  cpm: number;
  width: number;
  height: number;
  ad: string;
  creativeId?: string;
  ttl: number;
  netRevenue: boolean;
  currency: string;
  nurl?: string;
  meta?: { advertiserDomains?: string[]; mediaType?: 'banner' | 'video' | 'native' };
}

/**
 * isBidRequestValid is called by Prebid for every bid before requesting.
 * Reject anything that won't form a valid OpenRTB imp on our side.
 */
export function isBidRequestValid(bid: PrebidBidRequest): boolean {
  if (!bid || !bid.params) return false;
  const adUnitId = bid.params.adUnitId;
  if (adUnitId == null) return false;
  if (typeof adUnitId !== 'number' && typeof adUnitId !== 'string') return false;
  if (!resolveSize(bid)) return false;
  return true;
}

/**
 * buildRequests collapses every valid Prebid bid request for this adapter
 * into a single OpenRTB BidRequest. We send one server hit per auction.
 */
export function buildRequests(
  validBidRequests: PrebidBidRequest[],
  bidderRequest: PrebidBidderRequest,
): PrebidServerRequest | null {
  const head = validBidRequests[0];
  if (!head) return null;

  // All bids in the same Prebid auction share an auctionId — use that as
  // the OpenRTB BidRequest.id so server-side logs can correlate.
  const auctionId = bidderRequest.auctionId ?? head.auctionId;

  const tmax = Math.max(50, Math.min(bidderRequest.timeout ?? 300, 1000));
  const endpoint = head.params.endpoint ?? DEFAULT_ENDPOINT;

  const imps = validBidRequests.map((b) => buildImp(b));
  const bcat = collectStringSet(validBidRequests, (b) => b.params.bcat);
  const badv = collectStringSet(validBidRequests, (b) => b.params.badv);
  const testMode = validBidRequests.some((b) => b.params.test === 1) ? 1 : 0;

  const ortb: Record<string, unknown> = {
    id: auctionId,
    imp: imps,
    tmax,
    at: 1, // first-price by default; override at the server via env if desired
    cur: [DEFAULT_CURRENCY],
    test: testMode,
  };
  if (bcat.length) ortb.bcat = bcat;
  if (badv.length) ortb.badv = badv;
  const site = buildSite(bidderRequest);
  if (site) ortb.site = site;
  const regs = buildRegs(bidderRequest);
  if (regs) ortb.regs = regs;
  const user = buildUser(validBidRequests, bidderRequest);
  if (user) ortb.user = user;

  return {
    method: 'POST',
    url: endpoint,
    data: ortb,
    options: { contentType: 'application/json', withCredentials: true },
    bids: validBidRequests,
  };
}

/**
 * interpretResponse converts our OpenRTB BidResponse into Prebid's bid shape.
 * Per Prebid contract: return an array of bids (one per winning imp).
 */
export function interpretResponse(
  serverResponse: { body?: { seatbid?: Array<{ seat?: string; bid?: ServerBid[] }>; cur?: string } },
  request: PrebidServerRequest,
): PrebidInterpretedBid[] {
  if (!serverResponse?.body?.seatbid?.length) return [];
  const cur = serverResponse.body.cur || DEFAULT_CURRENCY;
  const impToBidId = new Map<string, PrebidBidRequest>();
  for (const b of request.bids) {
    impToBidId.set(b.bidId, b);
  }

  const out: PrebidInterpretedBid[] = [];
  for (const seat of serverResponse.body.seatbid) {
    for (const bid of seat.bid ?? []) {
      const orig = impToBidId.get(bid.impid);
      if (!orig) continue;
      const meta: PrebidInterpretedBid['meta'] = { mediaType: 'banner' };
      if (bid.adomain) meta.advertiserDomains = bid.adomain;
      const interpreted: PrebidInterpretedBid = {
        requestId: orig.bidId,
        cpm: Number(bid.price ?? 0),
        width: Number(bid.w ?? sizeFor(orig)[0]),
        height: Number(bid.h ?? sizeFor(orig)[1]),
        ad: bid.adm ?? '',
        ttl: 360,
        netRevenue: true,
        currency: cur,
        meta,
      };
      if (bid.crid) interpreted.creativeId = bid.crid;
      if (bid.nurl) interpreted.nurl = bid.nurl;
      out.push(interpreted);
    }
  }
  return out;
}

/** Optional user-sync hook. We don't run a cookie sync today. */
export function getUserSyncs(): never[] {
  return [];
}

// ---- spec adapter export (compatible with `pbjs.registerBidAdapter`) -------

export const spec = {
  code: BIDDER_CODE,
  supportedMediaTypes: ['banner'],
  isBidRequestValid,
  buildRequests,
  interpretResponse,
  getUserSyncs,
};

// Auto-register when bundled into Prebid.js. Guarded so the file can also
// be imported into unit tests without a `pbjs` global.
declare const pbjs: undefined | { registerBidAdapter?: (a: unknown, code: string) => void };
if (typeof pbjs !== 'undefined' && typeof pbjs?.registerBidAdapter === 'function') {
  pbjs.registerBidAdapter(spec, BIDDER_CODE);
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface ServerBid {
  id: string;
  impid: string;
  price: number;
  adm?: string;
  crid?: string;
  w?: number;
  h?: number;
  nurl?: string;
  adomain?: string[];
}

function buildImp(b: PrebidBidRequest): Record<string, unknown> {
  const [w, h] = resolveSize(b) ?? [300, 250];
  const imp: Record<string, unknown> = {
    id: b.bidId,
    tagid: String(b.params.adUnitId),
    banner: { w, h },
    secure: 1,
  };
  return imp;
}

function buildSite(req: PrebidBidderRequest): Record<string, unknown> | null {
  const r = req.refererInfo;
  if (!r) return null;
  const out: Record<string, unknown> = {};
  if (r.page) out.page = r.page;
  if (r.domain) out.domain = r.domain;
  return Object.keys(out).length ? out : null;
}

function buildRegs(req: PrebidBidderRequest): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  if (req.gdprConsent) {
    out.gdpr = req.gdprConsent.gdprApplies ? 1 : 0;
    if (req.gdprConsent.consentString) {
      out.ext = { consent: req.gdprConsent.consentString };
    }
  }
  if (req.uspConsent) {
    out.us_privacy = req.uspConsent;
  }
  return Object.keys(out).length ? out : null;
}

function buildUser(
  bids: PrebidBidRequest[],
  _req: PrebidBidderRequest,
): Record<string, unknown> | null {
  const eids: Array<{ source: string; uids: Array<{ id: string }> }> = [];
  for (const b of bids) {
    if (b.userIdAsEids) {
      eids.push(...b.userIdAsEids);
    }
  }
  if (!eids.length) return null;
  return { ext: { eids } };
}

function resolveSize(b: PrebidBidRequest): [number, number] | null {
  const banner = b.mediaTypes?.banner?.sizes?.[0];
  if (banner && Array.isArray(banner) && banner.length === 2) return [banner[0], banner[1]];
  const flat = b.sizes?.[0];
  if (flat && Array.isArray(flat) && flat.length === 2) return [flat[0], flat[1]];
  return null;
}

function sizeFor(b: PrebidBidRequest): [number, number] {
  return resolveSize(b) ?? [300, 250];
}

function collectStringSet(
  bids: PrebidBidRequest[],
  pick: (b: PrebidBidRequest) => string[] | undefined,
): string[] {
  const set = new Set<string>();
  for (const b of bids) {
    const arr = pick(b);
    if (!arr) continue;
    for (const v of arr) set.add(v);
  }
  return [...set];
}
